import React, { useMemo } from 'react';

// Tokens a resaltar = todo lo que se pueda contrastar contra AURUM:
//  - cada barra IG (numero o "IG-XXXX")
//  - gramos finos AU/AG por barra y leyes
//  - peso bruto por barra
//  - totales (gramos finos AU, gramos finos AG, peso bruto total)
//  - numero de factura y de VB
// Devuelve variantes de formato del MISMO valor numérico que vienen de
// AURUM, en estilo inglés (12,345.67) y español (12.345,67), con y sin
// separador de miles, y con distintas cantidades de decimales:
//   - Decimales >= los originales: se agregan ceros al final (mismo valor).
//   - Decimales <  los originales: se TRUNCAN (no se redondea) los dígitos
//     finales, para tolerar que el texto SAE muestre el mismo valor con
//     menos precisión (ej. AURUM 29016.43 → SAE "29,016.4").
// El resaltado parcial dentro de un número mayor lo previenen los bordes
// estrictos del regex en highlightText (un token "14,226" NO matchea
// dentro de "14,226.09" porque el "." siguiente está bloqueado).
function numberVariants(n) {
  if (n == null || n === '' || isNaN(n)) return [];
  const raw = String(n).trim();
  const v = Number(raw);
  const dotIdx = raw.indexOf('.');
  const origDecimals = dotIdx >= 0 ? raw.length - dotIdx - 1 : 0;
  const maxD = Math.max(origDecimals, 4);
  const out = new Set();
  for (let d = 0; d <= maxD; d++) {
    let fixed;
    if (d >= origDecimals) {
      // Sólo agrega ceros: usar toFixed conserva el valor exacto.
      fixed = v.toFixed(d);
    } else {
      // Trunca dígitos (no redondea) para reflejar lo que la SAE pudo
      // imprimir con menos precisión.
      const factor = Math.pow(10, d);
      const truncated = (v >= 0 ? Math.floor(v * factor) : Math.ceil(v * factor)) / factor;
      fixed = truncated.toFixed(d);
    }
    const [intPart, decPart] = fixed.split('.');
    const intEN = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const intES = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    // Sin separador de miles
    out.add(decPart ? `${intPart}.${decPart}` : intPart);
    out.add(decPart ? `${intPart},${decPart}` : intPart);
    // Inglés: 12,345.67
    out.add(decPart ? `${intEN}.${decPart}` : intEN);
    // Español: 12.345,67
    out.add(decPart ? `${intES},${decPart}` : intES);
  }
  return Array.from(out);
}

// Devuelve variantes de una fecha aceptando entradas en formato
// DD/MM/YYYY, D/M/YYYY, YYYY-MM-DD, YYYY/MM/DD (con separadores '/' o '-').
// Genera las 4 combinaciones de padding en formato D[/-]M[/-]YYYY que la
// SAE puede imprimir (ej. 5/03/2026, 05/3/2026, 5/3/2026, 05/03/2026)
// usando '/' y '-' como separador.
function dateVariants(input) {
  if (!input) return [];
  const s = String(input).trim();
  let dd, mm, yyyy;
  let m = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/); // YYYY-MM-DD
  if (m) {
    yyyy = m[1]; mm = m[2]; dd = m[3];
  } else {
    m = s.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/); // DD/MM/YYYY
    if (!m) return [];
    dd = m[1]; mm = m[2]; yyyy = m[3];
  }
  const ddPad = dd.padStart(2, '0');
  const mmPad = mm.padStart(2, '0');
  const ddNum = String(parseInt(dd, 10));
  const mmNum = String(parseInt(mm, 10));
  const out = new Set();
  for (const sep of ['/', '-']) {
    for (const d of [ddPad, ddNum]) {
      for (const mo of [mmPad, mmNum]) {
        out.add(`${d}${sep}${mo}${sep}${yyyy}`);
      }
    }
    // Tambien YYYY-MM-DD por si aparece tal cual.
    out.add(`${yyyy}${sep}${mmPad}${sep}${ddPad}`);
  }
  return Array.from(out);
}

function buildTokens({ barras, factura, vbNumero, vbTramite, vbFechaExpedicion, vbFechaVencimiento, totales }) {
  const tokens = new Set();
  const addNum = (n) => {
    for (const v of numberVariants(n)) tokens.add(v);
  };

  if (Array.isArray(barras)) {
    for (const b of barras) {
      if (b.barra_ig) {
        const ig = String(b.barra_ig).trim();
        tokens.add(ig);
        if (/^\d+$/.test(ig)) tokens.add(`IG-${ig}`);
      }
      addNum(b.gr_au);
      addNum(b.ley_au);
      addNum(b.gr_ag);
      addNum(b.ley_ag);
      addNum(b.peso_bruto_gr);
    }
  }
  if (totales) {
    addNum(totales.totalGrAu);
    addNum(totales.totalGrAg);
    addNum(totales.totalPesoBruto);
  }
  if (factura) tokens.add(String(factura).trim());
  if (vbNumero) tokens.add(String(vbNumero).trim());
  // Tramite del Visto Bueno (TM-XXXXXXXX-YYYYMMDD): aparece en la serie
  // como "CON TRAMITE TM-...".
  if (vbTramite) tokens.add(String(vbTramite).trim());
  // Fechas del VB: la SAE las suele imprimir como "FE 5/03/2026" o
  // "FF 25/06/2026"; el VB puede entregarlas como DD/MM/YYYY o YYYY-MM-DD.
  for (const f of [vbFechaExpedicion, vbFechaVencimiento]) {
    for (const v of dateVariants(f)) tokens.add(v);
  }
  return Array.from(tokens).filter((t) => t && String(t).length >= 3);
}

function highlightText(text, tokens) {
  if (!text || !tokens || tokens.length === 0) return [{ text, match: false }];
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sorted = [...tokens].sort((a, b) => b.length - a.length);
  // Para tokens cortos (<=3 chars) o que sean numéricos (solo dígitos y
  // separadores ,.- ), exigimos límites estrictos para evitar coincidencias
  // parciales dentro de otros números. Ej: "14,226" NO debe matchear dentro
  // de "14,226.09" cuando AURUM trae 14226.10.
  const buildAlt = (tok) => {
    const e = esc(tok);
    const isShort = tok.length <= 3;
    const isNumericLike = /^[\d.,\-]+$/.test(tok);
    if (isShort || isNumericLike) {
      return `(?<![\\w.,\\-])${e}(?![\\w.,\\-])`;
    }
    return e;
  };
  const pattern = new RegExp(`(${sorted.map(buildAlt).join('|')})`, 'gi');
  const parts = text.split(pattern);
  const lower = new Set(tokens.map((t) => t.toLowerCase()));
  return parts.map((p) => ({ text: p, match: !!p && lower.has(p.toLowerCase()) }));
}

function calcTotales(barras) {
  if (!Array.isArray(barras) || barras.length === 0) return null;
  let totalGrAu = 0;
  let totalGrAg = 0;
  let totalPesoBruto = 0;
  for (const b of barras) {
    totalGrAu += Number(b.gr_au) || 0;
    totalGrAg += Number(b.gr_ag) || 0;
    totalPesoBruto += Number(b.peso_bruto_gr) || 0;
  }
  return {
    totalGrAu: Math.round(totalGrAu * 100) / 100,
    totalGrAg: Math.round(totalGrAg * 100) / 100,
    totalPesoBruto: Math.round(totalPesoBruto * 100) / 100,
  };
}

function HighlightedBlock({ titulo, descripcion, segments, footerNote, extraInfo }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="bg-gold-500 text-white px-5 py-2.5 font-semibold text-sm">
        {titulo}
      </div>
      {extraInfo && (
        <div className="px-5 pt-3 text-xs text-gray-600 flex flex-wrap gap-x-6 gap-y-1">
          {extraInfo}
        </div>
      )}
      <div className="px-5 py-4 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">
        {descripcion ? (
          segments.map((s, i) =>
            s.match ? (
              <mark key={i} className="bg-green-200 text-green-900 rounded px-0.5">
                {s.text}
              </mark>
            ) : (
              <span key={i}>{s.text}</span>
            )
          )
        ) : (
          <span className="text-gray-400 italic">Sin descripción.</span>
        )}
      </div>
      {footerNote && (
        <div className="px-5 pb-3 text-xs text-gray-500">{footerNote}</div>
      )}
    </div>
  );
}

export default function SaeDescripcion({
  descripcion,
  subpartida,
  barras,
  factura,
  vbNumero,
  vbTramite,
  vbFechaExpedicion,
  vbFechaVencimiento,
}) {
  const totales = useMemo(() => calcTotales(barras), [barras]);
  const cantidadBarras = Array.isArray(barras) ? barras.length : 0;

  const tokensSerie = useMemo(
    () => buildTokens({ barras, factura, vbNumero, vbTramite, vbFechaExpedicion, vbFechaVencimiento, totales }),
    [barras, factura, vbNumero, vbTramite, vbFechaExpedicion, vbFechaVencimiento, totales]
  );

  // Para "subpartidas_declaradas" resaltamos los mismos datos contrastables
  // contra AURUM que ya destacamos en la serie: barras IG, gramos finos
  // AU/AG, leyes, pesos brutos por barra, totales (gr AU, gr AG, peso bruto)
  // y la cantidad de barras.
  const tokensSubpartida = useMemo(() => {
    const t = new Set();
    if (cantidadBarras > 0) t.add(String(cantidadBarras));
    const addVariants = (n) => {
      for (const v of numberVariants(n)) t.add(v);
    };
    if (Array.isArray(barras)) {
      for (const b of barras) {
        if (b.barra_ig) {
          const ig = String(b.barra_ig).trim();
          t.add(ig);
          if (/^\d+$/.test(ig)) t.add(`IG-${ig}`);
        }
        addVariants(b.gr_au);
        addVariants(b.ley_au);
        addVariants(b.gr_ag);
        addVariants(b.ley_ag);
        addVariants(b.peso_bruto_gr);
      }
    }
    if (totales) {
      addVariants(totales.totalPesoBruto);
      addVariants(totales.totalGrAu); // gramos finos ORO totales
      addVariants(totales.totalGrAg); // gramos finos PLATA totales
    }
    // Numero de Visto Bueno: si en la subpartida aparece algo como
    // "APROBADO VB-ANM-20260325-00056" y coincide con el VB cargado,
    // tambien lo resaltamos.
    if (vbNumero) t.add(String(vbNumero).trim());
    if (factura) t.add(String(factura).trim());
    if (vbTramite) t.add(String(vbTramite).trim());
    for (const f of [vbFechaExpedicion, vbFechaVencimiento]) {
      for (const v of dateVariants(f)) t.add(v);
    }
    return Array.from(t).filter((x) => x && String(x).length >= 1);
  }, [barras, cantidadBarras, totales, vbNumero, factura, vbTramite, vbFechaExpedicion, vbFechaVencimiento]);

  const segSerie = useMemo(
    () => highlightText(descripcion || '', tokensSerie),
    [descripcion, tokensSerie]
  );
  const segSub = useMemo(
    () => highlightText((subpartida && subpartida.descripcion) || '', tokensSubpartida),
    [subpartida, tokensSubpartida]
  );

  if (!descripcion && !(subpartida && subpartida.descripcion)) return null;

  return (
    <div className="space-y-6">
      {descripcion && (
        <HighlightedBlock
          titulo="Descripción de la SAE — Datos de la Serie (coincidencias resaltadas)"
          descripcion={descripcion}
          segments={segSerie}
          footerNote="Resaltado en verde: número de barra IG, gramos finos AU/AG, leyes, peso bruto, totales, número de factura, número de VB, trámite (TM-) y fechas de expedición/vencimiento del VB."
        />
      )}

      {subpartida && subpartida.descripcion && (
        <HighlightedBlock
          titulo="Subpartidas declaradas (coincidencias resaltadas)"
          descripcion={subpartida.descripcion}
          segments={segSub}
          extraInfo={
            <>
              <span>
                <strong className="text-gray-700">No. Grupo:</strong> 1{' '}
                <span className="text-blue-600">(fijo)</span>
              </span>
              <span>
                <strong className="text-gray-700">No. Serie:</strong> 1{' '}
                <span className="text-blue-600">(fijo)</span>
              </span>
              <span>
                <strong className="text-gray-700">Cantidad de barras IG:</strong>{' '}
                {cantidadBarras}
              </span>
            </>
          }
          footerNote="Resaltado en verde: cantidad de barras, peso bruto total y cantidad de unidades comerciales (gramos finos de ORO)."
        />
      )}
    </div>
  );
}
